<ng-container *ngIf="canViewPayment$ && claim$ | async as claim">
  <ng-container *vsLet="canManagePayments$ | async as canManagePayments">
    <ng-container *vsLet="canManagePaymentMethods$ | async as canManagePaymentMethods">
      <ng-container *vsLet="customer$ | async as customer">
        <ng-container *vsLet="customerId$ | async as customerId">
          <vs-card *vsLet="fundingSources$ | async as fundingSources" theme="default" class="flex flex-col gap-4">
            <ng-container *vsLet="holds$ | async as holds">
              <ng-container *vsLet="paymentsAndItems$ | async as payments">
                <vs-card-header
                  [config]="{ title: 'Payments', subtitle: 'Manage and view all payments for this claim below.' }"
                >
                  <vs-badge slot="after-title" theme="default">
                    {{ paginationConfig.totalItems || 0 }} payment{{ paginationConfig.totalItems !== 1 ? 's' : '' }}
                  </vs-badge>

                  <div class="flex gap-2 items-center" slot="end">
                    <button
                      *ngIf="canManagePayments && payments?.length && canCreatePayment(claim, holds)"
                      vs-button
                      theme="primary"
                      (click)="paymentDialog?.open(claim)"
                    >
                      <vs-icon name="plus"></vs-icon>
                      New Payment
                    </button>

                    <button
                      *ngIf="canManageLink$ | async"
                      vs-icon-button
                      appearance="outline"
                      [vsMenuTriggerFor]="linkMenu"
                    >
                      <vs-icon name="link-external-01" size="20"></vs-icon>

                      <ng-template #linkMenu>
                        <vs-menu class="mt-2">
                          <button vs-menu-item (click)="copyLink(claim.warranty?.contractNumber ?? '', claim.id)">
                            Copy manage payment method link
                          </button>
                        </vs-menu>
                      </ng-template>
                    </button>
                  </div>
                </vs-card-header>

                <div *ngIf="claim.isAuthorizationRequired !== false" class="px-6">
                  <ng-container *ngIf="claim.paymentBalances?.length; else notAuthorized">
                    <!-- Approvals first: the net payable one comes with the authorization, before any split. -->
                    <ws-claim-overrides
                      [claim]="claim"
                      [overrides]="holds?.overrides ?? []"
                      [paidTargetIds]="holds?.paidTargetIds ?? []"
                      [canManage]="(canManageOverrides$ | async) ?? false"
                      (changed)="refresh()"
                    ></ws-claim-overrides>

                    <!-- Each authorization, the first one and its supplementals, has its own balance and split. -->
                    <section
                      *ngFor="let balance of claim.paymentBalances; let index = index; let first = first; let count = count"
                      class="flex flex-col gap-3 mt-4 rounded-2xl border bg-gray-50 p-4"
                    >
                      <header class="flex flex-wrap items-center justify-between gap-2">
                        <div class="flex items-center gap-2 min-w-0">
                          <vs-icon
                            size="18"
                            class="shrink-0 text-primary-500"
                            [name]="first ? 'check-verified-02' : 'file-plus-03'"
                          ></vs-icon>
                          <p class="text-sm font-semibold text-gray-900 whitespace-nowrap">
                            <!-- Original and supplemental only mean something once there's more than one. -->
                            {{ count === 1 ? 'Authorization' : first ? 'Original authorization' : 'Supplemental ' +
                            index }}
                          </p>
                          <p class="text-sm text-gray-500 truncate">· {{ balance.authorizationReference }}</p>
                        </div>
                        <div class="flex items-center gap-2">
                          <span
                            *ngIf="authorizedOn(claim, balance.authorizationId) as created"
                            class="text-xs text-gray-500"
                          >
                            {{ created | date: 'mediumDate' }}
                          </span>
                          <vs-badge [theme]="balance.remaining > 0 ? 'warn' : 'success'">
                            {{ balance.remaining > 0 ? formatAmount(balance.remaining) + ' left' : 'Fully allocated' }}
                          </vs-badge>
                        </div>
                      </header>

                      <dl class="grid grid-cols-3 border rounded-xl divide-x bg-base-white">
                        <div class="flex flex-col gap-1 px-4 py-3">
                          <dt class="text-xs text-gray-500">Authorized</dt>
                          <dd class="text-sm font-semibold text-gray-900">
                            {{ formatAmount(balance.authorizedPayable) }}
                          </dd>
                        </div>
                        <div class="flex flex-col gap-1 px-4 py-3">
                          <dt class="text-xs text-gray-500">Paid</dt>
                          <dd class="text-sm font-semibold text-gray-900">{{ formatAmount(balance.paid) }}</dd>
                        </div>
                        <div class="flex flex-col gap-1 px-4 py-3">
                          <dt class="text-xs text-gray-500">Remaining</dt>
                          <dd
                            class="text-sm font-semibold"
                            [class.text-gray-900]="balance.remaining > 0"
                            [class.text-success-700]="balance.remaining <= 0"
                          >
                            {{ balance.remaining > 0 ? formatAmount(balance.remaining) : 'Fully allocated' }}
                          </dd>
                        </div>
                      </dl>

                      <ws-claim-allocations
                        class="block"
                        [claim]="claim"
                        [authorizationId]="balance.authorizationId"
                        [canManage]="(canManageAllocations$ | async) ?? false"
                        [hold]="heldAuthorization(holds, balance.authorizationId)"
                        (changed)="refresh()"
                      ></ws-claim-allocations>
                    </section>
                  </ng-container>

                  <ng-template #notAuthorized>
                    <div
                      class="flex flex-wrap sm:flex-nowrap items-center gap-3 rounded-xl border border-warn-200 bg-warn-50 px-4 py-3"
                    >
                      <div
                        class="w-10 min-w-[40px] h-10 flex items-center justify-center bg-base rounded-lg border border-warn-200"
                      >
                        <vs-icon name="lock-01" size="20" class="text-warn-700"></vs-icon>
                      </div>
                      <div class="flex-1">
                        <p class="text-sm font-semibold text-warn-700">Waiting for authorization</p>
                        <p class="text-sm text-warn-700">
                          Payments can be prepared once the claim is authorized on the Overview tab.
                        </p>
                      </div>
                      <a vs-button appearance="outline" size="sm" routerLink=".." class="shrink-0">Go to Overview</a>
                    </div>
                  </ng-template>
                </div>

                <ng-container *ngIf="!loading; else isLoading">
                  <vs-card-content class="!pt-0">
                    <!-- Every payment, of every authorization, apart from the sections above. -->
                    <div *ngIf="payments?.length" class="flex flex-col mt-6 mb-3">
                      <p class="text-sm font-semibold text-gray-900">Payment history</p>
                      <p class="text-xs text-gray-500">
                        Every payment on this claim, with the authorization it pays from.
                      </p>
                    </div>

                    <ws-claim-payments-table
                      *ngIf="payments?.length; else noPayments"
                      [claim]="claim"
                      [customer]="customer"
                      [payments]="payments"
                      [canEdit]="canManagePayments"
                      [destinations]="fundingSources"
                      [customerId]="customerId?.id ?? null"
                      [paginationConfig]="paginationConfig"
                      [canIssue]="canIssuePayments$ | async"
                      [canVoid]="canFinishPayments$ | async"
                      [canManagePaymentMethods]="canManagePaymentMethods"
                      (refresh)="refresh()"
                      (refreshPaymentMethods)="refreshPaymentMethods()"
                      (paginationQueryRequest)="paginationQueryRequest$.next($event)"
                    ></ws-claim-payments-table>

                    <ng-template #noPayments>
                      <div class="flex flex-col justify-center gap-2 p-6">
                        <p class="text-xl font-semibold text-gray-900 self-center">
                          No payments have been made for this claim.
                        </p>
                        <p class="font-normal text-gray-600 self-center mb-4">
                          Once a payment has been made, its details will appear here.
                        </p>

                        <button
                          vs-button
                          appearance="outline"
                          class="self-center"
                          *ngIf="canManagePayments && canCreatePayment(claim, holds)"
                          (click)="paymentDialog?.open(claim)"
                        >
                          <vs-icon name="bank-note-01"></vs-icon> New Payment
                        </button>

                        <!-- Says why New Payment is missing, so it's clear what to do. -->
                        <p
                          *ngIf="canManagePayments && holds?.reason"
                          class="self-center text-sm text-warn-700 text-center"
                        >
                          {{ holds?.reason }}
                        </p>
                      </div>
                    </ng-template>
                  </vs-card-content>
                </ng-container>

                <ng-template #isLoading>
                  <div class="flex items-center justify-center min-h-[350px]">
                    <vs-icon name="loading-01" class="animate-spin text-primary-500" size="40"></vs-icon>
                  </div>
                </ng-template>
              </ng-container>
            </ng-container>

            <ws-claim-payment-dialog
              [customer]="customer"
              [customerId]="customerId?.id ?? null"
              [destinations]="fundingSources"
              [canManagePaymentMethods]="canManagePaymentMethods"
              (refresh)="refresh()"
              (refreshPaymentMethods)="refreshPaymentMethods()"
            ></ws-claim-payment-dialog>
          </vs-card>
        </ng-container>
      </ng-container>
    </ng-container>
  </ng-container>
</ng-container>

<router-outlet></router-outlet>
